// @ts-strict-ignore
import type { CrudServiceListResponse } from '@empathyco/playboard-core-domain'
import type { RegularFilterConfig } from '../../models'
import type { IToolingBaseComposableComputed, IToolingBaseComposableDataItem } from './types'
import { computed, getCurrentInstance } from 'vue'
import { TOOLING_CONSTS } from '../../store/tooling/tooling.consts'
import { useFeature } from '../use-feature'
import { useFilters } from '../use-filters'
import { useToolingPivots } from '../use-tooling-pivots'
import { mapGetters } from '../utils'
import { getRegularFiltersConfig } from './utils'

export const computedFn = <
  DataItemType extends IToolingBaseComposableDataItem,
>(): IToolingBaseComposableComputed<DataItemType> => {
  const {
    proxy: { $store },
  } = getCurrentInstance()

  const { featureId, readonly } = useFeature()
  const { availableFilters, isFilterVisible } = useFilters()
  const { pivots, visiblePivots } = useToolingPivots()

  const { state } = mapGetters($store, TOOLING_CONSTS.moduleName, {
    state: TOOLING_CONSTS.getters.state,
  })

  const toolConfig = computed(() => {
    return state.value[featureId.value]?.config
  })

  const filtersConfig = computed((): Record<string, RegularFilterConfig> => {
    return getRegularFiltersConfig(availableFilters.value)
  })

  const visibleFiltersConfig = computed<Record<string, RegularFilterConfig>>(() =>
    Object.values(filtersConfig.value).reduce<Record<string, RegularFilterConfig>>(
      (result, filterConfig) => {
        if (isFilterVisible(filterConfig)) {
          result[filterConfig.id] = filterConfig
        }
        return result
      },
      {},
    ),
  )

  const pivotsConfig = computed<Record<string, RegularFilterConfig>>(() =>
    pivots.value.reduce<Record<string, RegularFilterConfig>>(
      (result, pivot) => ({
        ...result,
        [pivot.config.id]: pivot.config,
      }),
      {},
    ),
  )

  const visiblePivotsConfig = computed<Record<string, RegularFilterConfig>>(() =>
    visiblePivots.value.reduce<Record<string, RegularFilterConfig>>(
      (result, pivot) => ({
        ...result,
        [pivot.config.id]: pivot.config,
      }),
      {},
    ),
  )

  const data = computed((): CrudServiceListResponse<DataItemType> => {
    return state.value[featureId.value]?.data ?? { results: [], numFound: 0 }
  })

  return {
    featureId,
    readonly,
    toolConfig,
    filtersConfig,
    visibleFiltersConfig,
    pivotsConfig,
    visiblePivotsConfig,
    data,
  }
}
